import { TenantReadDtoPageDto } from '../_generated/api/user-management'
import ApiModel from './ApiModel'

// NOTE: Using provided page dto, which is common for all paginated payloads.
//  Ideally, we would have a generic, unnamed type for this purpose instead.
export interface PageDto<D> extends Omit<TenantReadDtoPageDto, 'items'> {
    items: D[]
}

export const DEFAULT_PAGINATION_DATA = {
    hasNextPage: false,
    hasPreviousPage: false,
    isFirstPage: true,
    isLastPage: true,
    pageNumber: 1,
    pageSize: 0,
    skip: 0,
    take: 50,
    totalCount: 0,
    totalPages: 0,
    items: [],
}

/**
 * Generic pagination wrapper for API data with enabled pagination.
 * This model offsets the DTO type provided by the API into a list of ApiModel types.
 *
 * Note that the DTO type defaults to `any` because this allows it to be omitted for brevity.
 *
 * @example `Pagination<Model, ModelDto>` results in the type `ApiModel<PageDto<ModelDto>>`, where items map to the `Model` type.
 */
export default class Pagination<T, D = any> extends ApiModel<PageDto<D>> {
    protected fn: () => void = () => undefined
    public items: T[] = []

    static empty<T>() {
        return new Pagination<T>(DEFAULT_PAGINATION_DATA)
    }

    static get infinite() {
        // NOTE: API accepts taking a list with the length of a signed 32 bit integer max value.
        return { skip: 0, take: 2147483647 }
    }

    get pageNumber() {
        return this.data?.pageNumber ?? DEFAULT_PAGINATION_DATA.pageNumber
    }

    get pageSize() {
        return this.data?.pageSize ?? DEFAULT_PAGINATION_DATA.pageSize
    }

    get totalCount() {
        return this.data?.totalCount ?? DEFAULT_PAGINATION_DATA.totalCount
    }

    get totalPages() {
        return this.data?.totalPages ?? DEFAULT_PAGINATION_DATA.totalPages
    }

    get isFirstPage() {
        return this.data?.isFirstPage ?? DEFAULT_PAGINATION_DATA.isFirstPage
    }

    get isLastPage() {
        return this.data?.isLastPage ?? DEFAULT_PAGINATION_DATA.isLastPage
    }

    get hasNextPage() {
        return this.data?.hasNextPage ?? DEFAULT_PAGINATION_DATA.hasNextPage
    }

    get hasPreviousPage() {
        return this.data?.hasPreviousPage ?? DEFAULT_PAGINATION_DATA.hasPreviousPage
    }

    get skip() {
        return this.data?.skip ?? DEFAULT_PAGINATION_DATA.skip
    }

    set skip(value) {
        this.data.skip = value
    }

    get take() {
        return this.data?.take ?? DEFAULT_PAGINATION_DATA.take
    }

    set take(value) {
        this.data.take = value
    }

    get index() {
        return this.pageNumber - 1
    }

    get first() {
        return this.index * this.pageSize
    }

    get hasMultiplePages() {
        return this.totalCount > this.pageSize
    }

    get hasAnyPage() {
        return this.totalCount > 0
    }

    set refresh(fn: () => void) {
        this.fn = fn
    }

    constructor(
        public data: PageDto<D>,
        model?: new (data: D) => T
    ) {
        super(data)
        if (model) this.items = this.data.items.map((d) => new model(d))
        else this.items = DEFAULT_PAGINATION_DATA.items
    }

    next() {
        this.skip += this.take
        this.fn()
    }

    prev() {
        this.skip -= this.take
        this.skip = Math.max(0, this.skip)
        this.fn()
    }

    page(page: number) {
        this.skip = this.take * page
        this.fn()
    }
}
